/* SK Anime Cross-Category Upsell — replaces UpCart's "You'll love these"
   with same-anime products from different categories (LEDs, rugs, tapestries)
   Supports multi-anime carts. Injects into UpCart's Shadow DOM. */
(function () {
  'use strict';

  var CACHE = {};
  var LAST_CART_SIG = '';
  var DEBOUNCE_TIMER = null;
  var PROCESSING = false;

  var CATEGORY_KEYWORDS = {
    'mini-figure': ['mini figure', 'minifigure', 'mini-figure', 'custom figure', 'giant figure'],
    'led':         ['led', 'light box', 'lightbox', 'rgb'],
    'rug':         ['rug'],
    'tapestry':    ['tapestry', 'tapestries', 'woven tapestry'],
    'keychain':    ['keychain', 'key chain', 'key strap', 'keystrap', 'embroidered keychain'],
    'poster':      ['poster', '3d poster'],
    'apparel':     ['shirt', 'hoodie', 'jersey', 'lever belt']
  };

  var CROSS_SELL_TARGETS = {
    'mini-figure': ['tapestry', 'led', 'rug'],
    'led':         ['tapestry', 'rug'],
    'rug':         ['tapestry', 'led'],
    'tapestry':    ['led', 'rug'],
    'keychain':    ['tapestry', 'led', 'rug'],
    'poster':      ['tapestry', 'led', 'rug'],
    'apparel':     ['tapestry', 'led', 'rug'],
    'default':     ['tapestry', 'led', 'rug']
  };

  var COLLECTION_SUFFIX = { 'led': '-led-boxes', 'rug': '-rugs' };

  var UPSELL_CSS = [
    '.sk-au { padding: 0 16px; }',
    '.sk-au-title { font-size: 16px; font-weight: 700; text-align: center; margin: 12px 0 8px; font-family: inherit; }',
    '.sk-au-grid { display: flex; flex-direction: column; gap: 10px; }',
    '.sk-au-card { display: flex; align-items: center; gap: 12px; padding: 10px; border: 1px solid #eee; border-radius: 10px; background: #fafafa; }',
    '.sk-au-img { flex: 0 0 70px; width: 70px; height: 70px; border-radius: 8px; overflow: hidden; background: #fff; border: 1px solid #eee; }',
    '.sk-au-img img { width: 100%; height: 100%; object-fit: cover; display: block; }',
    '.sk-au-info { flex: 1; min-width: 0; }',
    '.sk-au-name { font-size: 13px; font-weight: 700; line-height: 1.2; margin: 0 0 2px; overflow: hidden; text-overflow: ellipsis; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }',
    '.sk-au-type { font-size: 10px; color: #888; text-transform: uppercase; letter-spacing: 0.5px; margin: 0 0 3px; }',
    '.sk-au-prices { font-size: 13px; display: flex; align-items: center; gap: 5px; }',
    '.sk-au-price { font-weight: 800; color: #000; }',
    '.sk-au-compare { text-decoration: line-through; color: #999; font-size: 11px; }',
    '.sk-au-save { background: #ffd2d2; color: #ff1717; font-weight: 800; font-size: 10px; padding: 1px 6px; border-radius: 50px; }',
    '.sk-au-btn { flex: 0 0 auto; padding: 8px 16px; background: #9B2624; color: #fff; border: 0; border-radius: 8px; font-weight: 700; font-size: 12px; cursor: pointer; transition: background .15s; white-space: nowrap; }',
    '.sk-au-btn:hover { background: #b32d2b; }',
    '.sk-au-btn:disabled { background: #ccc; cursor: default; }',
    '.sk-au-btn--added { background: #2d7d2d; }',
    '.sk-au-btn--added:hover { background: #2d7d2d; }'
  ].join('\n');

  function slugify(s) {
    return s.toLowerCase().replace(/['']/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  function detectCategory(product) {
    var combined = ((product.title || '') + ' ' + (product.product_type || '')).toLowerCase();
    for (var cat in CATEGORY_KEYWORDS) {
      var kws = CATEGORY_KEYWORDS[cat];
      for (var i = 0; i < kws.length; i++) {
        if (combined.indexOf(kws[i]) !== -1) return cat;
      }
    }
    return 'default';
  }

  function formatMoney(amount) {
    var n = parseFloat(amount);
    if (isNaN(n)) return '$0';
    return '$' + n.toFixed(n % 1 === 0 ? 0 : 2);
  }

  function fetchJSON(url) {
    if (CACHE[url]) return Promise.resolve(CACHE[url]);
    return fetch(url).then(function (r) {
      if (!r.ok) return null;
      return r.json();
    }).then(function (data) {
      if (data) CACHE[url] = data;
      return data;
    }).catch(function () { return null; });
  }

  function getCart() {
    return fetch('/cart.js').then(function (r) {
      return r.ok ? r.json() : null;
    }).catch(function () { return null; });
  }
  function getProduct(handle) { return fetchJSON('/products/' + handle + '.js'); }

  function getCollectionProducts(handle, limit) {
    return fetchJSON('/collections/' + handle + '/products.json?limit=' + (limit || 8))
      .then(function (d) { return d && d.products ? d.products : []; });
  }

  function findAnimeSlug(product) {
    var tags = product.tags || [];
    if (typeof tags === 'string') tags = tags.split(',').map(function (t) { return t.trim(); });

    var slugs = tags.map(function (t) { return { tag: t, slug: slugify(t) }; })
      .filter(function (s) { return s.slug.length >= 2; });

    function tryNext(i) {
      if (i >= slugs.length) return Promise.resolve(null);
      return getCollectionProducts(slugs[i].slug + '-led-boxes', 1).then(function (prods) {
        if (prods.length > 0) return slugs[i];
        return tryNext(i + 1);
      });
    }
    return tryNext(0);
  }

  function getTapestryProducts(animeTag) {
    return getCollectionProducts('woven-tapestries', 50).then(function (prods) {
      var lowerTag = animeTag.toLowerCase();
      return prods.filter(function (p) {
        var tags = p.tags || [];
        if (typeof tags === 'string') tags = tags.split(',').map(function (t) { return t.trim(); });
        for (var i = 0; i < tags.length; i++) {
          if (tags[i].toLowerCase() === lowerTag) return true;
        }
        return false;
      });
    });
  }

  function getCrossUpsells(animeSlug, animeTag, cartCategory, cartHandles, maxTotal) {
    maxTotal = maxTotal || 3;
    var targets = CROSS_SELL_TARGETS[cartCategory] || CROSS_SELL_TARGETS['default'];
    var seenHandles = {};
    var perCat = {};

    function getProdsForCat(target) {
      if (target === 'tapestry') return getTapestryProducts(animeTag);
      var suffix = COLLECTION_SUFFIX[target];
      if (!suffix) return Promise.resolve([]);
      return getCollectionProducts(animeSlug + suffix, 8);
    }

    function fetchAll(i) {
      if (i >= targets.length) return Promise.resolve();
      return getProdsForCat(targets[i]).then(function (prods) {
        var filtered = [];
        for (var j = 0; j < prods.length; j++) {
          var p = prods[j];
          if (cartHandles[p.handle]) continue;
          if (seenHandles[p.handle]) continue;
          var hasAvailableVariant = p.variants && p.variants.some(function (v) { return v.available; });
          if (!hasAvailableVariant) continue;
          p._upsellCat = targets[i];
          seenHandles[p.handle] = true;
          filtered.push(p);
        }
        perCat[targets[i]] = filtered;
        return fetchAll(i + 1);
      });
    }

    return fetchAll(0).then(function () {
      // 1 from each category first, then backfill
      var results = [];
      for (var t = 0; t < targets.length && results.length < maxTotal; t++) {
        if (perCat[targets[t]] && perCat[targets[t]].length) {
          results.push(perCat[targets[t]].shift());
        }
      }
      // Backfill remaining slots from any category in priority order
      for (var t2 = 0; t2 < targets.length && results.length < maxTotal; t2++) {
        while (perCat[targets[t2]] && perCat[targets[t2]].length && results.length < maxTotal) {
          results.push(perCat[targets[t2]].shift());
        }
      }
      return results;
    });
  }

  function catLabel(cat) {
    var map = { 'led': 'LED BOX', 'rug': 'RUG', 'tapestry': 'TAPESTRY', 'mini-figure': 'MINI FIGURE' };
    return map[cat] || cat.toUpperCase();
  }

  function buildCard(product) {
    var v = product.variants && (product.variants.find(function (x) { return x.available; }) || product.variants[0]);
    if (!v) return '';
    var price = v.price;
    var compare = v.compare_at_price;
    var hasDiscount = compare && parseFloat(compare) > parseFloat(price);
    var savePct = hasDiscount ? Math.round((parseFloat(compare) - parseFloat(price)) / parseFloat(compare) * 100) : 0;
    var rawImg = (product.images && product.images[0]) || product.featured_image || '';
    var img = (typeof rawImg === 'object' && rawImg !== null) ? (rawImg.src || '') : rawImg;

    return [
      '<div class="sk-au-card" data-handle="' + product.handle + '">',
      '  <div class="sk-au-img"><img src="' + img + '" alt="" loading="lazy"></div>',
      '  <div class="sk-au-info">',
      '    <div class="sk-au-name">' + product.title + '</div>',
      '    <div class="sk-au-type">' + catLabel(product._upsellCat || '') + '</div>',
      '    <div class="sk-au-prices">',
      '      <span class="sk-au-price">' + formatMoney(price) + '</span>',
             hasDiscount ? '<span class="sk-au-compare">' + formatMoney(compare) + '</span>' : '',
             hasDiscount ? '<span class="sk-au-save">-' + savePct + '%</span>' : '',
      '    </div>',
      '  </div>',
      '  <button class="sk-au-btn" data-vid="' + v.id + '">Add</button>',
      '</div>'
    ].join('\n');
  }

  function addToCart(variantId, btn) {
    btn.disabled = true;
    btn.textContent = '...';
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] })
    }).then(function () {
      btn.textContent = 'Added ✓';
      btn.classList.add('sk-au-btn--added');
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      try { if (window.UpCart && window.UpCart.refreshCart) window.UpCart.refreshCart(); } catch (e) {}
    }).catch(function () {
      btn.disabled = false;
      btn.textContent = 'Add';
    });
  }

  function injectIntoShadow(shadow, products, animeTag) {
    var old = shadow.getElementById('sk-anime-upsells');
    if (old) old.remove();
    var oldStyle = shadow.getElementById('sk-anime-upsell-css');
    if (oldStyle) oldStyle.remove();

    var style = document.createElement('style');
    style.id = 'sk-anime-upsell-css';
    style.textContent = UPSELL_CSS;
    shadow.appendChild(style);

    var existingUpsell = shadow.querySelector('.upcart-internal-upsell');
    if (!existingUpsell) return;

    existingUpsell.style.display = 'none';

    var section = document.createElement('div');
    section.id = 'sk-anime-upsells';
    section.className = 'sk-au';
    var titleText;
    if (animeTag) {
      var displayTag = animeTag.replace(/\b\w/g, function (c) { return c.toUpperCase(); });
      titleText = 'Complete Your ' + displayTag + ' Collection';
    } else {
      titleText = 'Complete Your Collection';
    }
    section.innerHTML = '<div class="sk-au-title">' + titleText + '</div>' +
      '<div class="sk-au-grid">' + products.map(buildCard).join('') + '</div>';

    section.querySelectorAll('.sk-au-btn').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        addToCart(btn.dataset.vid, btn);
      });
    });

    existingUpsell.parentElement.insertBefore(section, existingUpsell);
  }

  function processCart() {
    if (PROCESSING) return;
    var upCartEl = document.getElementById('upCart');
    if (!upCartEl || !upCartEl.shadowRoot) return;
    var shadow = upCartEl.shadowRoot;

    PROCESSING = true;
    getCart().then(function (cart) {
      if (!cart || !cart.items || !cart.items.length) {
        var defaultUpsell = shadow.querySelector('.upcart-internal-upsell');
        if (defaultUpsell) defaultUpsell.style.display = '';
        var ourSection = shadow.getElementById('sk-anime-upsells');
        if (ourSection) ourSection.remove();
        PROCESSING = false;
        return;
      }
      // Hide UpCart's default upsells to prevent flash while loading anime recs
      var existingDefault = shadow.querySelector('.upcart-internal-upsell');
      if (existingDefault && !shadow.getElementById('sk-anime-upsells')) {
        existingDefault.style.display = 'none';
      }

      var sig = cart.items.map(function (i) { return i.handle + ':' + i.quantity; }).join('|');
      if (sig === LAST_CART_SIG) {
        var alreadyInjected = shadow.getElementById('sk-anime-upsells');
        if (alreadyInjected) { PROCESSING = false; return; }
      }

      var cartHandles = {};
      cart.items.forEach(function (i) { cartHandles[i.handle] = true; });

      // Find ALL distinct anime in cart
      var items = cart.items.slice();
      var animeList = [];
      var seenSlugs = {};

      function findAllAnime(idx) {
        if (idx >= items.length) return Promise.resolve(animeList);
        return getProduct(items[idx].handle).then(function (product) {
          if (!product) return findAllAnime(idx + 1);
          return findAnimeSlug(product).then(function (anime) {
            if (anime && !seenSlugs[anime.slug]) {
              seenSlugs[anime.slug] = true;
              animeList.push({ anime: anime, product: product });
            }
            return findAllAnime(idx + 1);
          });
        });
      }

      return findAllAnime(0).then(function (allAnime) {
        if (!allAnime.length) {
          var existing = shadow.querySelector('.upcart-internal-upsell');
          if (existing) existing.style.display = '';
          var ourSection = shadow.getElementById('sk-anime-upsells');
          if (ourSection) ourSection.remove();
          return;
        }

        // Get cross-sell products for each anime
        var perAnime = [];
        function getForAnime(i) {
          if (i >= allAnime.length) return Promise.resolve(perAnime);
          var entry = allAnime[i];
          var cartCat = detectCategory(entry.product);
          return getCrossUpsells(entry.anime.slug, entry.anime.tag, cartCat, cartHandles, 3).then(function (upsells) {
            if (upsells.length) {
              perAnime.push({ anime: entry.anime, upsells: upsells });
            }
            return getForAnime(i + 1);
          });
        }

        return getForAnime(0).then(function (allResults) {
          if (!allResults.length) {
            var existing = shadow.querySelector('.upcart-internal-upsell');
            if (existing) existing.style.display = '';
            return;
          }

          // Interleave products from each anime round-robin style
          var finalProducts = [];
          var maxTotal = 3;
          var round = 0;
          while (finalProducts.length < maxTotal) {
            var added = false;
            for (var a = 0; a < allResults.length && finalProducts.length < maxTotal; a++) {
              if (round < allResults[a].upsells.length) {
                finalProducts.push(allResults[a].upsells[round]);
                added = true;
              }
            }
            if (!added) break;
            round++;
          }

          var titleTag = allResults.length === 1 ? allResults[0].anime.tag : null;
          injectIntoShadow(shadow, finalProducts, titleTag);
          if (shadow.getElementById('sk-anime-upsells')) LAST_CART_SIG = sig;
        });
      });
    }).catch(function (err) {
      console.error('[sk-anime-upsell]', err);
    }).then(function () {
      PROCESSING = false;
    });
  }

  function debouncedProcess() {
    clearTimeout(DEBOUNCE_TIMER);
    DEBOUNCE_TIMER = setTimeout(processCart, 400);
  }

  function watchUpCart() {
    var upCartEl = document.getElementById('upCart');
    if (!upCartEl) {
      var bodyObs = new MutationObserver(function () {
        var el = document.getElementById('upCart');
        if (el) {
          bodyObs.disconnect();
          watchUpCart();
        }
      });
      bodyObs.observe(document.body || document.documentElement, { childList: true, subtree: true });
      return;
    }

    function onShadowReady() {
      var shadow = upCartEl.shadowRoot;
      if (!shadow) return;

      var shadowObs = new MutationObserver(function () {
        var cartCard = shadow.querySelector('.upcart-cart');
        if (cartCard) {
          debouncedProcess();
        }
      });
      shadowObs.observe(shadow, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style'] });

      debouncedProcess();
    }

    if (upCartEl.shadowRoot) {
      onShadowReady();
    } else {
      var pollCount = 0;
      var pollInterval = setInterval(function () {
        if (upCartEl.shadowRoot || pollCount > 100) {
          clearInterval(pollInterval);
          if (upCartEl.shadowRoot) onShadowReady();
        }
        pollCount++;
      }, 200);
    }
  }

  ['cart:refresh', 'upcart:cart:refresh', 'cart:build', 'cart:added'].forEach(function (evt) {
    document.addEventListener(evt, function () {
      LAST_CART_SIG = '';
      setTimeout(debouncedProcess, 600);
    });
    window.addEventListener(evt, function () {
      LAST_CART_SIG = '';
      setTimeout(debouncedProcess, 600);
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchUpCart);
  } else {
    watchUpCart();
  }
})();
